import React, {FC, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useTheme } from "styled-components";
import { billingService } from "../../../../../../api";
import { InfoBlock, Select, Typography, Table, Button } from "../../../../../../components";
import { BodyMedium } from "../../../../../../components/typography/styled";
import { useUi } from "../../../../../../context/ui.context";
import { icons } from "../../../../../../icons";
import { convertUTCtoLocalDate } from "../../../../../../utils/dates";
import { formatCurrency } from "../../../../../../utils/display-price";
import {
  Container,
  OptionsWrapper,
  PeriodWapper,
  DateRangeCOntainer,
  PeriodContainer,
  ArrowImage,
  ResourceName,
  ResourceNameContainer,
  TotalPriceWrapper,
  TotalPriceContainer,
  CustomBodyTable,
  CustomBodyTitle,
  Divider,
} from './styles'
import HistorySkeleton from './skeleton'

const BillingHistory:FC = () => {

  const [t] = useTranslation()
  const { userId } = useParams();
  const {viewAlert} = useUi()
  const theme:any = useTheme()
  const [billingData, setBillingData] = useState<any>()
  const [billingDate, setBillingDate] = useState<any>()
  const [availableServices, setAvailableServices] = useState<any[]>([])
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [selectedService, setSelectedService] = useState<any>()
  const [usageId, setUsageId] = useState<any>(null)
  const [usage, setUsage] = useState<any>(null)
  const [currentUsageId, setCurrentUsageId] = useState<any>(null)
  const isSameUsage = (firstId:any, secondId:any) => {
    return firstId != null && secondId != null && String(firstId) === String(secondId)
  }
  const getCurrentUsageId = (data:any) => {
    return data?.estimated_cycle_cost?.usage_id ?? data?.objects?.[0]?.id ?? null
  }
  const getHistoryUsageId = (historyId:any) => {
    if(typeof historyId !== 'string') return historyId

    const pathParts = historyId.split('/').filter(Boolean)
    return pathParts[pathParts.length - 1]?.split('?')?.[0]
  }
  const selectCycle = (resBilling:any) => {
    const selectedUsage = resBilling?.data?.objects?.[0]

    if(!selectedUsage){
      setBillingDate('')
      setBillingData({
        ...resBilling?.data?.credit_and_invoicing_info,
        ...resBilling?.data?.estimated_cycle_cost,
        count:0,
        usage:null,
      })
      return
    }

    setBillingDate(`${convertUTCtoLocalDate(selectedUsage.start_date)} - ${convertUTCtoLocalDate(selectedUsage.billing_end)}`)
    setBillingData({
      ...resBilling?.data?.credit_and_invoicing_info,
      ...resBilling?.data?.estimated_cycle_cost,
      count:Math.max((resBilling?.data?.count ?? 0) -1, 0),
      usage:selectedUsage.usage,
    })
  }
  const mapAvailableServices = (services:any[] = []) => services.map((service:any) => ({
    ...service,
    id:service.id ?? service.service_id,
    label:service.display,
  }))
  const applyBillingResponse = (res:any) => {
    const currentUsageId = getCurrentUsageId(res?.data)

    setCurrentUsageId(currentUsageId)
    setUsage(res?.data?.objects?.[0])
    selectCycle(res)
  }
  const handlePreviousUsage = () => {
    if(usage?.previous_history){
      setUsageId(getHistoryUsageId(usage.previous_history))
    }
  }
  const handleNextUsage = () => {
    let nextUsageId = null

    if(usage?.next_history){
      nextUsageId = getHistoryUsageId(usage.next_history)
    }else if(!isSameUsage(usage?.id, currentUsageId)){
      nextUsageId = currentUsageId
    }

    if(nextUsageId != null){
      setUsageId(nextUsageId)
    }
  }
  const usageUpdate = async () => {
    try{
      if(!usageId) return
      const res = await billingService.getBillingById(usageId)
      setBillingDate(`${convertUTCtoLocalDate(res.data.start_date)} - ${convertUTCtoLocalDate(res.data.billing_end)}`)
      setUsage(res?.data)
      setBillingData((prev:any) => ({
        ...prev,
        usage:res?.data?.usage,
      }))
    }catch(err:any) {
      console.log('err',err);
    }
  }
  useEffect(() => {
    usageUpdate()
  },[usageId])
  const getBillingData = (serviceId:string | number) => {
    setPageLoading(true)
    setUsageId(null)

    return billingService.getBillingHistory({
      page:1,
      page_size:1,
      client: userId,
      service_id:serviceId,
    })
    .then((res) => {
      applyBillingResponse(res)
    })
    .catch((err) => {
      console.log('err', err);
      if(err?.response?.data?.detail){
        viewAlert({
          severity: 'error',
          message: err.response.data.detail
        })
      }
    })
    .finally(() => {
      setPageLoading(false)
    })
  }
  const getLegacyBillingData = () => {
    return billingService.getBillingHistory({
      page:1,
      page_size:1,
      client:userId,
    })
    .then((res) => {
      const serviceOptions = mapAvailableServices(res?.data?.available_services)

      setAvailableServices(serviceOptions)
      setSelectedService(serviceOptions[0]?.id)
      applyBillingResponse(res)
    })
    .catch((err) => {
      console.log('err', err)
      if(err?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:err.response.data.detail,
        })
      }
    })
    .finally(() => {
      setPageLoading(false)
    })
  }
  const getAvailableServices = () => {
    setPageLoading(true)

    if(!userId){
      setPageLoading(false)
      return
    }

    billingService.getAvailableBillingServices(userId)
    .then((res) => {
      const serviceOptions = mapAvailableServices(res?.data)
      const firstServiceId = serviceOptions[0]?.id

      setAvailableServices(serviceOptions)
      setSelectedService(firstServiceId)

      if(firstServiceId == null){
        setUsage(null)
        setCurrentUsageId(null)
        setPageLoading(false)
        return
      }

      return getBillingData(firstServiceId)
    })
    .catch((err) => {
      if(err?.response?.status === 404){
        getLegacyBillingData()
        return
      }

      console.log('err', err)
      if(err?.response?.data?.detail){
        viewAlert({
          severity: 'error',
          message: err.response.data.detail
        })
      }
      setPageLoading(false)
    })
  }
  useEffect(() => {
    getAvailableServices()
  },[])
  const Columns = [
    {
      title:t("NAME"),
      key:'name'
    },
    {
      title:t("PRICE"),
      key:'price'
    },
  ]
  if(pageLoading) return <HistorySkeleton pageLoading={pageLoading}/>
  const isCurrentUsage = isSameUsage(usage?.id, currentUsageId)
  const usageDetails = usage?.usage?.usage_details ?? []
  const usageCurrencyCode = usage?.client_currency?.code ?? billingData?.usage?.currency?.code

  return (
    <Container>
      <Typography variant="h1">
        {t("BILLING_HISTORY")}
      </Typography>
      {(availableServices.length > 0 || usage) ?
        <OptionsWrapper>
          <Select
            data={availableServices}
            title={t("SERVICES")}
            selectedValue={selectedService}
            onChange={(serviceId) => {
              setSelectedService(serviceId)
              getBillingData(serviceId)
            }}
          />
          {usage ?
            <PeriodContainer>
              <Typography variant='body-medium'>
                {t("PERIOD")}
              </Typography>
              <PeriodWapper>
                <Button
                  onClick={handlePreviousUsage}
                  variant='icon'
                  size='display'
                  disabled={!usage?.previous_history}
                  customStyles={{ width: '30px', backgroundColor:theme.palette[10], height: "30px"}}
                >
                  <ArrowImage src={icons.ArrowLeft}/>
                </Button>
                <DateRangeCOntainer>
                  <Typography variant='body'>
                    {billingDate}
                  </Typography>
                </DateRangeCOntainer>
                <Button
                  onClick={handleNextUsage}
                  variant='icon'
                  size='display'
                  disabled={isCurrentUsage || (!usage?.next_history && currentUsageId == null)}
                  customStyles={{ width: '30px', backgroundColor:theme.palette[10], height: "30px"}}
                >
                  <ArrowImage src={icons.ArrowRight}/>
                </Button>
              </PeriodWapper>
            </PeriodContainer>
          : null}
        </OptionsWrapper>
      : null}
      {!usage ?
        <Typography variant="body">
          {t("NO_DATA")}
        </Typography>
      : null}
      {usageDetails.map((history:any) => (
        <InfoBlock
          dropped
          noTitles
          //@ts-ignore
          data={[]}
          customDropped={
            <Table
              columns={Columns}
              hasDroppedRow
              rows={(history.usage ?? []).map((usageItem:any) => {
                const usageItemHistory = usageItem.history ?? []

                return{
                  ...usageItem,
                  hasHistory:usageItemHistory.length > 0,
                  customBody:(
                    <CustomBodyTable>
                      <CustomBodyTitle>
                        {t("USAGE_HISTORY")}
                      </CustomBodyTitle>
                      {usageItemHistory.map((usageHistoryItem:any, index:number) => {
                        if (!usageHistoryItem?.price) return null;
                        const historyPrice = Number(usageHistoryItem.price);
                        const normalizedPrice = Number.isFinite(historyPrice) ? historyPrice.toFixed(2) : "0.00";
                        return (
                          <DateRangeCOntainer key={`${usageHistoryItem?.id ?? usageHistoryItem?.name}-${index}`}>
                            <Typography  variant="body">
                              {`${usageHistoryItem.name} - ${usageHistoryItem.price_details.timespan} `}
                            </Typography>
                            <BodyMedium style={{whiteSpace:'nowrap'}}>
                              {formatCurrency(normalizedPrice, usageCurrencyCode)}
                            </BodyMedium>
                          </DateRangeCOntainer>
                        )
                      })}
                    </CustomBodyTable>
                  ),
                  price:formatCurrency(usageItem.price, usageCurrencyCode),
                  name:history.resource_name === 'Floating IPs' || history.resource_name === 'Volume' ? 
                    (
                      <ResourceNameContainer>
                        <Typography variant='body'>
                          {usageItem.display_name}
                        </Typography>
                      </ResourceNameContainer>
                    )
                  :
                    (
                      <ResourceNameContainer>
                        <ResourceName>
                          {history.resource_name}:
                        </ResourceName>
                        <Typography variant='body'>
                          {usageItem.display_name}
                        </Typography>
                      </ResourceNameContainer>
                    )
                }
              })}
              hideOther
              hideCheckbox
            />
          }
          title={<div style={{display:'flex', width:'100%', justifyContent:'space-between', alignItems:"center"}}>
            <Typography variant="h3">
            {history.resource_name}
            </Typography>
            <Typography variant="body">
              {formatCurrency(history.price, usageCurrencyCode)}
            </Typography>
          </div>}
          customStyles={{flex:'auto 1', marginRight:'10px'}}
        />
      ))}
      {usage ?
        <TotalPriceWrapper>
          <TotalPriceContainer>
            <Typography variant='body'>
              {t("TOTAL_PRICE")}
            </Typography>
            <Typography variant='h3'>
              {/* @ts-ignore */}
              {formatCurrency(usage?.price, usageCurrencyCode)}
            </Typography>
          </TotalPriceContainer>
          {isCurrentUsage ? <Divider/> : null}
          {isCurrentUsage ?
            <TotalPriceContainer>
              <Typography variant='body'>
                {t("ESTIMATED_CYCLE_HOST")}
              </Typography>
              <Typography variant='h3'>
                {formatCurrency(billingData?.amount, usageCurrencyCode)}
              </Typography>
            </TotalPriceContainer>
          : null}
        </TotalPriceWrapper>
      : null}
    </Container>
  )
}

export default BillingHistory
